import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/lib/AuthContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { toast } from "sonner";
import { COUNTRY_LIST } from "@/lib/iban";
import { ArrowRight, ShieldCheck } from "lucide-react";

export default function Register() {
  const { t } = useTranslation();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    password: "",
    country: "ES",
    accept: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.accept) {
      setError(t("auth.must_accept"));
      return;
    }
    setLoading(true);
    setError("");
    const r = await register({
      full_name: form.full_name,
      email: form.email,
      password: form.password,
      country: form.country,
    });
    setLoading(false);
    if (r.ok) {
      toast.success(t("auth.toast_registered"));
      navigate("/app/kyc");
    } else {
      setError(r.error);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-cosmos" data-testid="register-page">
      <div className="hidden lg:flex flex-col justify-between text-white p-12 relative overflow-hidden bg-cosmos-dim">
        <Link to="/" className="flex items-center gap-2">
          <span className="block w-2 h-2 rounded-full pulse-dot" style={{ background: "linear-gradient(135deg,#8B5CF6,#3B82F6)" }}></span>
          <span className="font-display font-bold text-2xl text-white">
            Open<span className="brand-text-light">Payments</span>
          </span>
        </Link>
        <div className="relative z-10">
          <h2 className="font-display font-bold text-4xl tracking-tighter leading-tight">
            {t("auth.reg_left_title")}
          </h2>
          <ul className="mt-6 space-y-3 text-slate-300">
            {[t("auth.reg_left_b1"), t("auth.reg_left_b2"), t("auth.reg_left_b3"), t("auth.reg_left_b4")].map((x) => (
              <li key={x} className="flex items-center gap-3"><ShieldCheck size={18} className="text-blue-400" /> {x}</li>
            ))}
          </ul>
        </div>
        <div className="font-mono-fin text-xs text-slate-500 relative z-10">
          FCA · MiCA · GDPR · PSD2
        </div>
        <div className="absolute -right-20 -bottom-20 w-[400px] h-[400px] border border-blue-600/20 rounded-full"></div>
      </div>

      <div className="flex flex-col justify-center p-8 lg:p-16 max-w-md mx-auto w-full relative">
        <div className="absolute top-4 right-4">
          <LanguageSwitcher tone="dark" />
        </div>
        <div className="font-mono-fin text-xs uppercase tracking-widest text-blue-600 mb-3">{t("auth.register_eyebrow")}</div>
        <h1 className="font-display font-bold text-4xl tracking-tighter text-slate-900">{t("auth.register_title")}</h1>
        <p className="text-slate-500 mt-3">{t("auth.register_subtitle")}</p>

        <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="register-form">
          <div>
            <label className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">{t("auth.full_name")}</label>
            <input required value={form.full_name} onChange={(e) => set("full_name", e.target.value)}
                   className="w-full mt-2 px-4 py-3 border border-slate-200 focus:border-blue-600 focus:ring-2 focus:ring-blue-100 outline-none text-slate-900"
                   data-testid="reg-fullname" placeholder="María González Ruiz" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">{t("auth.email")}</label>
              <input type="email" required value={form.email} onChange={(e) => set("email", e.target.value)}
                     className="w-full mt-2 px-4 py-3 border border-slate-200 focus:border-blue-600 focus:ring-2 focus:ring-blue-100 outline-none text-slate-900"
                     data-testid="reg-email" placeholder="tu@email.com" />
            </div>
            <div>
              <label className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">{t("auth.country")}</label>
              <select value={form.country} onChange={(e) => set("country", e.target.value)}
                      className="w-full mt-2 px-3 py-3 border border-slate-200 focus:border-blue-600 outline-none text-slate-900 bg-white"
                      data-testid="reg-country">
                {COUNTRY_LIST.map((c) => (
                  <option key={c.code} value={c.code}>{c.flag} {c.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">{t("auth.password")}</label>
            <input type="password" required minLength={6} value={form.password} onChange={(e) => set("password", e.target.value)}
                   className="w-full mt-2 px-4 py-3 border border-slate-200 focus:border-blue-600 focus:ring-2 focus:ring-blue-100 outline-none text-slate-900 font-mono-fin"
                   data-testid="reg-password" placeholder="Min. 6" />
          </div>
          <label className="flex items-start gap-3 text-sm text-slate-600">
            <input type="checkbox" checked={form.accept} onChange={(e) => set("accept", e.target.checked)}
                   className="mt-1" data-testid="reg-accept" />
            <span>
              {t("auth.accept_terms_pre")} <Link to="/terminos" className="text-blue-600 hover:underline">{t("auth.accept_terms_link")}</Link>{t("auth.accept_terms_post")}
            </span>
          </label>
          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2" data-testid="reg-error">{error}</div>
          )}
          <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="reg-submit">
            {loading ? t("auth.creating") : <>{t("auth.register_submit")} <ArrowRight size={18} /></>}
          </button>
        </form>

        <div className="mt-8 text-sm text-slate-500">
          {t("auth.have_account")}{" "}
          <Link to="/login" className="text-blue-600 font-semibold hover:underline">{t("auth.do_login")}</Link>
        </div>
      </div>
    </div>
  );
}
